import { useMemo } from "react";
import { Clock, CalendarDays, CalendarCheck } from "lucide-react";
import { Input } from "./ui/input";

/**
 * SchedulePicker — a 3-mode cadence chooser for the Log Shot dialog.
 *
 * Modes:
 *   - "hours"    → every N hours (existing behavior; sub-daily protocols)
 *   - "days"     → every N days  (presets: 1, 2, 3, 7 weekly, 14 biweekly)
 *   - "weekdays" → specific ISO weekdays (Mon=1 … Sun=7). Multi-select.
 *
 * Emits {type, value, weekdays} through `onChange`. Value is `null` for
 * weekdays mode; weekdays is `null` for the other two.
 *
 * Backward-compat: if `value` is missing or falsy, defaults to hours mode.
 */
const DAY_PRESETS = [
  { d: 1,  label: "Daily" },
  { d: 2,  label: "Every 2d" },
  { d: 3,  label: "Every 3d" },
  { d: 7,  label: "Weekly" },
  { d: 14, label: "Biweekly" },
];

const WEEKDAY_CHIPS = [
  { iso: 1, letter: "M", full: "Monday" },
  { iso: 2, letter: "T", full: "Tuesday" },
  { iso: 3, letter: "W", full: "Wednesday" },
  { iso: 4, letter: "T", full: "Thursday" },
  { iso: 5, letter: "F", full: "Friday" },
  { iso: 6, letter: "S", full: "Saturday" },
  { iso: 7, letter: "S", full: "Sunday" },
];

export default function SchedulePicker({ value, onChange, testIdPrefix = "shot" }) {
  const v = value || { type: "hours", value: 24, weekdays: null };
  const mode = v.type || "hours";

  // Effective interval preview so users see the impact of their choice
  // regardless of which mode they picked.
  const previewLabel = useMemo(() => {
    if (mode === "hours") {
      const h = Number(v.value) || 0;
      if (h >= 24 && h % 24 === 0) return `Every ${h / 24} day${h / 24 === 1 ? "" : "s"}`;
      return `Every ${h} hour${h === 1 ? "" : "s"}`;
    }
    if (mode === "days") {
      const d = Number(v.value) || 0;
      if (d === 7) return "Once a week";
      if (d === 14) return "Every 2 weeks";
      return `Every ${d} day${d === 1 ? "" : "s"}`;
    }
    const wds = Array.isArray(v.weekdays) ? v.weekdays : [];
    if (!wds.length) return "Pick at least one day";
    if (wds.length === 7) return "Every day";
    const names = wds.slice().sort().map((i) => WEEKDAY_CHIPS.find((c) => c.iso === i)?.full.slice(0, 3)).join(" · ");
    return `${wds.length}× per week (${names})`;
  }, [mode, v.value, v.weekdays]);

  const setMode = (next) => {
    if (next === "hours") onChange({ type: "hours", value: Number(v.value) >= 1 && mode === "days" ? Number(v.value) * 24 : (Number(v.value) || 24), weekdays: null });
    else if (next === "days") onChange({ type: "days", value: mode === "hours" && Number(v.value) >= 24 ? Number(v.value) / 24 : 1, weekdays: null });
    else onChange({ type: "weekdays", value: null, weekdays: v.weekdays?.length ? v.weekdays : [1, 3, 5] });
  };

  const toggleWeekday = (iso) => {
    const cur = new Set(v.weekdays || []);
    if (cur.has(iso)) cur.delete(iso); else cur.add(iso);
    onChange({ type: "weekdays", value: null, weekdays: Array.from(cur).sort() });
  };

  return (
    <div className="space-y-2" data-testid={`${testIdPrefix}-schedule`}>
      {/* Mode tabs */}
      <div className="grid grid-cols-3 gap-1.5 p-1 bg-slate-100 rounded-lg" role="tablist" data-testid={`${testIdPrefix}-schedule-tabs`}>
        {[
          { key: "hours",    label: "Hours",    Icon: Clock },
          { key: "days",     label: "Days",     Icon: CalendarDays },
          { key: "weekdays", label: "Weekday",  Icon: CalendarCheck },
        ].map(({ key, label, Icon }) => {
          const active = mode === key;
          return (
            <button
              key={key}
              type="button"
              role="tab"
              aria-selected={active}
              onClick={() => setMode(key)}
              data-testid={`${testIdPrefix}-schedule-tab-${key}`}
              className={`flex items-center justify-center gap-1.5 py-2 rounded-md text-xs font-semibold uppercase tracking-widest transition-colors ${
                active ? "bg-white text-teal-800 shadow-sm dark:bg-slate-800 dark:text-teal-300" : "text-slate-500 hover:text-slate-700"
              }`}
            >
              <Icon className="w-3.5 h-3.5" />
              {label}
            </button>
          );
        })}
      </div>

      {/* Mode body */}
      {mode === "hours" && (
        <Input
          type="number"
          step="0.5"
          min="0.5"
          value={v.value ?? ""}
          onChange={(e) => onChange({ type: "hours", value: e.target.value === "" ? "" : parseFloat(e.target.value), weekdays: null })}
          placeholder="24"
          className="bg-slate-50 border-slate-200 rounded-md h-11 font-mono-num"
          data-testid={`${testIdPrefix}-schedule-hours-input`}
        />
      )}

      {mode === "days" && (
        <div className="space-y-2">
          <div className="flex flex-wrap gap-1.5" data-testid={`${testIdPrefix}-schedule-day-presets`}>
            {DAY_PRESETS.map(({ d, label }) => {
              const active = Number(v.value) === d;
              return (
                <button
                  key={d}
                  type="button"
                  onClick={() => onChange({ type: "days", value: d, weekdays: null })}
                  data-testid={`${testIdPrefix}-schedule-day-preset-${d}`}
                  className={`px-2.5 py-1 rounded-md text-xs font-semibold border transition-colors ${
                    active
                      ? "bg-teal-50 border-teal-500 text-teal-800 dark:bg-teal-900/30 dark:text-teal-300"
                      : "bg-white border-slate-200 text-slate-700 hover:border-teal-300"
                  }`}
                >
                  {label}
                </button>
              );
            })}
          </div>
          <Input
            type="number"
            step="0.5"
            min="0.5"
            value={v.value ?? ""}
            onChange={(e) => onChange({ type: "days", value: e.target.value === "" ? "" : parseFloat(e.target.value), weekdays: null })}
            placeholder="Custom days between shots"
            className="bg-slate-50 border-slate-200 rounded-md h-11 font-mono-num"
            data-testid={`${testIdPrefix}-schedule-days-input`}
          />
        </div>
      )}

      {mode === "weekdays" && (
        <div className="flex gap-1.5" data-testid={`${testIdPrefix}-schedule-weekday-row`}>
          {WEEKDAY_CHIPS.map(({ iso, letter, full }) => {
            const active = (v.weekdays || []).includes(iso);
            return (
              <button
                key={iso}
                type="button"
                onClick={() => toggleWeekday(iso)}
                title={full}
                aria-pressed={active}
                aria-label={full}
                data-testid={`${testIdPrefix}-schedule-weekday-${iso}`}
                className={`flex-1 h-11 rounded-md text-sm font-bold border-2 transition-colors ${
                  active
                    ? "bg-teal-500 border-teal-500 text-white dark:bg-teal-600 dark:border-teal-600"
                    : "bg-white border-slate-200 text-slate-500 hover:border-teal-300"
                }`}
              >
                {letter}
              </button>
            );
          })}
        </div>
      )}

      <div className="text-[11px] text-slate-500 font-mono-num" data-testid={`${testIdPrefix}-schedule-preview`}>
        {previewLabel}
      </div>
    </div>
  );
}
